import { useState } from 'react'
import Tooltip from '@/components/ui/Tooltip'
import Popover from '@/components/ui/Popover'

import { Icon } from '@/icons/Icon'
import { cn, scrollToSection } from '@/lib/utils'
import { formatDeal, formatEligibility } from '@/components/content/deals/helpers'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { MenuItem } from '@/helpers/menus'
import type { Deal } from '@/payload-types'

export type DealOverviewProps = {
  deals?: Deal[]
  deal: Deal
  invert?: boolean
  subMenu?: MenuItem[]
}

type DealEligibilityProps = Pick<Deal, 'eligibility' | 'terms'> & {
  invert?: boolean
  hideInfo?: boolean
}

export function DealOverview({ deal, subMenu, invert }: DealOverviewProps) {
  const { lg } = useBreakpoint()
  const [subMenuVisible, setSubMenuVisible] = useState(false)

  const formattedDeal = formatDeal(deal)

  return (
    <div className={cn('flex items-center gap-3 text-16', { 'md:text-18': !invert })}>
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white',
          {
            'border-gray-200 bg-white text-gray-950 dark:border-white/15 dark:bg-black dark:text-white':
              invert,
          },
        )}
      >
        <Icon name={formattedDeal.icon} />
      </div>
      <p
        className={cn('w-full truncate lg:max-w-[260px] [&>span]:font-medium [&>span]:text-white', {
          'text-gray-950 dark:text-white [&>span]:text-gray-950 dark:[&>span]:text-white': invert,
        })}
      >
        {formattedDeal.shortname}
      </p>

      {subMenu && subMenu.length > 0 && (
        <Popover
          menu={subMenu}
          position="down"
          align={lg ? 'left' : 'right'}
          visible={subMenuVisible}
          setVisible={setSubMenuVisible}
        >
          <span
            className={cn(
              'flex h-full w-full cursor-pointer items-center justify-center text-white/30 transition-transform duration-300 ease-in-out',
              subMenuVisible ? 'rotate-90' : '-rotate-90',
            )}
          >
            <Icon name="chevron-left" size={12} />
          </span>
        </Popover>
      )}
    </div>
  )
}

export function DealEligibility({
  eligibility,
  terms,
  invert,
  hideInfo = false,
}: DealEligibilityProps) {
  const formattedEligibility = formatEligibility(eligibility)

  if (formattedEligibility) {
    return (
      <div
        className={cn('flex items-center gap-3 text-16 text-white/60', {
          'md:text-18': !invert,
        })}
      >
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white',
            {
              'border-gray-200 bg-white text-gray-950 dark:border-white/15 dark:bg-black dark:text-white':
                invert,
            },
          )}
        >
          {formattedEligibility.icon}
        </div>
        <p className={cn('truncate', { 'text-gray-950 dark:text-white': invert })}>
          For{' '}
          <span
            className={cn('font-medium text-white', { 'text-gray-950 dark:text-white': invert })}
          >
            {formattedEligibility.label}
          </span>
          {formattedEligibility.only ? ' only' : null}
        </p>
        {!hideInfo && (
          <Tooltip
            text={terms ? terms : formattedEligibility.terms}
            className="mt-1.5 cursor-pointer text-white/30 transition-colors hover:text-primary-500"
            position="up"
            width={400}
          >
            <div
              onClick={() =>
                scrollToSection({ id: 'deal-terms', offset: 80, animate: true, hideHash: true })
              }
            >
              <Icon name="circle-info" />
            </div>
          </Tooltip>
        )}
      </div>
    )
  }
}
